import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { APP_TRANSLATIONS } from '../constants/translations';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import FeedbackWidget from './FeedbackWidget';
import CookieConsentBanner from './CookieConsentBanner';
import useAdminSession from '../useAdminSession';
import useAnonymousAuth from '../hooks/useAnonymousAuth';
import { auth } from '../firebase';
import { excludeThisDeviceFromAnalytics, trackLanguageChange } from '../analytics';
import { AlertProvider, AlertStack, useAlerts } from './AlertSystem';
import { getArticleBySlug, listCategories } from '../blogData';
import {
  aboutHref, applyPageMeta, authorHref, clearPageAlternates, getPageKind, getPageMeta, getUrlLocale, homeHref,
  readPreferredLocale, savePreferredLocale,
} from '../siteLocale';

// Shown while a route's lazy-loaded chunk (see AppRouter.jsx) is still
// downloading. Deliberately lightweight and sits only where the page content
// goes — header/footer/feedback widget are eager imports rendered outside
// this boundary, so they never disappear during a chunk load. A full screen
// tall, so the footer waits below the fold instead of jumping away from
// mid-screen when the page arrives (it was the site's whole layout shift).
function RouteLoadingFallback() {
  return (
    <div className="flex min-h-svh flex-1 items-center justify-center py-24">
      <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function SiteLayoutContent() {
  const session = useAdminSession();
  useAnonymousAuth(auth);
  const { pushAlert } = useAlerts();
  const location = useLocation();
  const navigate = useNavigate();
  // The URL decides the language (siteLocale.js); the preference only covers
  // pages whose URL doesn't say (admin, 404) and where "/" sends someone.
  const [preferredLang, setPreferredLang] = useState(readPreferredLocale);
  const lang = getUrlLocale(location.pathname) || preferredLang;
  const pageKind = getPageKind(location.pathname);

  // A device that has ever had an admin session is Fer's: stop counting it.
  useEffect(() => {
    if (session.isAdmin) excludeThisDeviceFromAnalytics();
  }, [session.isAdmin]);

  // Layout effect so the title is already right when RouteAnalytics sends the
  // page view. Articles and authors set their own tags once loaded
  // (ArticlePage, AuthorPage). An author page's server tags carry the same
  // data-page-metadata mark as the rest, so they're left alone here and
  // AuthorPage replaces them; clearing them now would leave the page without
  // hreflang while it loads.
  useLayoutEffect(() => {
    if (pageKind === 'author') {
      document.documentElement.lang = lang;
      return;
    }
    if (pageKind === 'article') {
      document.documentElement.lang = lang;
      clearPageAlternates();
      return;
    }
    applyPageMeta(getPageMeta(pageKind, lang), lang);
  }, [pageKind, lang]);

  const choosePreferredLang = (nextLang) => {
    savePreferredLocale(nextLang);
    setPreferredLang(nextLang);
  };

  const handleLanguageChange = async (nextLang) => {
    trackLanguageChange(nextLang);
    choosePreferredLang(nextLang);

    const pathParts = location.pathname.split('/').filter(Boolean);
    const hasLanguagePrefix = ['es', 'en', 'pt'].includes(pathParts[0]);
    const routeParts = hasLanguagePrefix ? pathParts.slice(1) : pathParts;

    if (pageKind === 'home') {
      navigate(`${homeHref(nextLang)}${location.search}`);
      return;
    }

    if (pageKind === 'about') {
      navigate(aboutHref(nextLang));
      return;
    }

    if (pageKind === 'author') {
      navigate(authorHref(routeParts[2], nextLang));
      return;
    }

    if (routeParts[0] !== 'blog') {
      return;
    }

    try {
      if (routeParts.length === 1) {
        navigate(nextLang === 'en' ? '/blog' : `/${nextLang}/blog`);
        return;
      }

      if (routeParts[1] === 'category' && routeParts[2]) {
        const categories = await listCategories();
        const currentCategorySlug = routeParts[2];
        const category = categories.find((item) => Object.values(item.slug || {}).includes(currentCategorySlug));
        const translatedSlug = category?.slug?.[nextLang] || currentCategorySlug;
        navigate(nextLang === 'en'
          ? `/blog/category/${translatedSlug}`
          : `/${nextLang}/blog/category/${translatedSlug}`);
        return;
      }

      const currentLocale = hasLanguagePrefix ? pathParts[0] : 'en';
      const article = await getArticleBySlug(currentLocale, routeParts[1]);
      const translatedSlug = article?.slug?.[nextLang];
      if (!translatedSlug) {
        pushAlert({ type: 'info', title: 'Language', message: 'This article is not available in that language yet.' });
        return;
      }

      navigate(nextLang === 'en' ? `/blog/${translatedSlug}` : `/${nextLang}/blog/${translatedSlug}`);
    } catch (error) {
      pushAlert({ type: 'error', title: 'Language', message: 'The translated page could not be opened.' });
    }
  };

  useEffect(() => {
    if (session.error) {
      pushAlert({
        type: 'error',
        title: 'Admin session',
        message: session.error,
      });
    }
  }, [pushAlert, session.error]);

  // "/" is the English hub: someone who picked Spanish/Portuguese, or whose
  // browser is in one of them and never picked, lands on /es or /pt instead.
  // Crawlers browse in English, so they keep indexing "/" as the English page.
  if (location.pathname === '/' && preferredLang !== 'en') {
    return <Navigate replace to={`${homeHref(preferredLang)}${location.search}${location.hash}`} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-night bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-night to-black text-white font-sans selection:bg-cyan-500/30 relative">
      {/* Keyboard users jump past the header straight to the page. */}
      <a href="#contenido" className="sr-only z-[300] rounded-full bg-cyan-300 px-4 py-2 text-sm font-bold text-night focus:not-sr-only focus:fixed focus:left-4 focus:top-3">
        {APP_TRANSLATIONS[lang]?.skipToContent || APP_TRANSLATIONS.en.skipToContent}
      </a>
      <SiteHeader session={session} lang={lang} onLanguageChange={handleLanguageChange} />
      <React.Suspense fallback={<RouteLoadingFallback />}>
        <Outlet context={{ lang, setLang: choosePreferredLang, session }} />
      </React.Suspense>
      {/* The admin is a work tool: no public footer (donation, socials) and no
          visitor feedback button there. */}
      {pageKind !== 'admin' && <SiteFooter lang={lang} />}
      {pageKind !== 'admin' && <FeedbackWidget lang={lang} />}
      <CookieConsentBanner lang={lang} />
    </div>
  );
}

export default function SiteLayout() {
  return (
    <AlertProvider>
      <AlertStack />
      <SiteLayoutContent />
    </AlertProvider>
  );
}